import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MXN } from "../lib/apiClient";
import { TAG_LABELS, TAG_STYLE } from "../lib/constants";
import { useFavorites } from "../hooks/useFavorites";
import { useCart } from "../hooks/useCart";
import { toast } from "sonner";
import { Heart, Plus, Sparkles } from "lucide-react";

export const ProductCard = ({ product, index = 0 }) => {
  const navigate = useNavigate();
  const { isFav, toggle } = useFavorites();
  const { addItem } = useCart();
  const img = product.images?.[0] || "";
  const fav = isFav(product.id);
  const tag = product.tags?.[0];
  const ts = tag ? (TAG_STYLE[tag] || { bg: "#4B1025", animated: false }) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.4, delay: (index % 4) * 0.06 }}
      data-testid={`product-card-${product.id}`}
      className="bg-blancoCalido border border-beige/60 rounded-[26px] p-3.5 relative shadow-[0_8px_24px_rgba(47,33,29,0.05)] hover:shadow-[0_16px_38px_rgba(118,27,56,0.13)] hover:-translate-y-1.5 transition-all duration-300 flex flex-col group"
    >
      <button data-testid={`fav-${product.id}`} onClick={(e) => { e.stopPropagation(); toggle(product.id); }}
        className="absolute top-5 right-5 z-10 bg-crema/90 backdrop-blur rounded-full p-2 shadow-sm hover:scale-110 active:scale-90 transition-transform">
        <Heart className={`w-4 h-4 transition-colors ${fav ? "fill-vino text-vino" : "text-vino/60"}`} />
      </button>

      <div onClick={() => navigate(`/producto/${product.id}`)} className="w-full aspect-square rounded-[20px] bg-beige/50 mb-3 overflow-hidden cursor-pointer relative">
        {img ? <img src={img} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
          : <div className="w-full h-full flex items-center justify-center text-rosa"><Sparkles /></div>}
        {tag && (
          <span className={`absolute top-2.5 left-2.5 z-20 text-crema text-[10px] font-semibold tracking-wide px-2.5 py-1 rounded-full shadow-sm ${ts.animated ? "badge-animated" : ""}`}
            style={{ background: ts.bg }}>
            <span className="badge-text">{TAG_LABELS[tag] || tag}</span>
          </span>
        )}
      </div>

      <div className="flex flex-col flex-1 px-1">
        <h3 onClick={() => navigate(`/producto/${product.id}`)} className="font-serif text-lg sm:text-xl text-vinoDark leading-tight cursor-pointer line-clamp-1">{product.name}</h3>
        <p className="text-xs text-cafe/55 mt-0.5 line-clamp-2 flex-1">{product.description}</p>
        <div className="mt-3 flex items-center justify-between">
          <span className="font-serif text-lg text-vino font-semibold">
            {product.requires_quote ? <span className="text-salvia text-sm">Cotización</span> : MXN(product.base_price)}
          </span>
          <button data-testid={`add-${product.id}`} onClick={(e) => {
            e.stopPropagation();
            addItem({
              product_id: product.id,
              product_name: product.name,
              image: img,
              quantity: 1,
              base_price: product.base_price,
              requires_quote: product.requires_quote,
            });
            toast.success("Agregado al carrito 💛", {
              action: { label: "Ver carrito", onClick: () => navigate("/carrito") },
            });
          }}
            className="bg-vinoDark text-crema rounded-full w-10 h-10 flex items-center justify-center shadow-md hover:bg-vino active:scale-90 transition-all">
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};
